import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAdminSummary, type RateLimitStats } from "@/hooks/useAdminSummary";
import { useAuth } from "@/state/auth";
import { formatDistanceToNow } from "date-fns";
import { apiRequest } from "@/utils/apiClient";
import { Button, PageHeader } from "@/components/ui";
import { Icon } from "@/components/IconSprite";

const Dashboard = () => {
  const fetchProfile = useAuth((state) => state.fetchProfile);
  const { data, isLoading } = useAdminSummary();
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();

  const [isEditingLimits, setIsEditingLimits] = useState(false);
  const [limitInputs, setLimitInputs] = useState({ perMinute: "15", perHour: "120", perDay: "600" });
  const [limitFormError, setLimitFormError] = useState<string | null>(null);

  const rateLimitMutation = useMutation({
    mutationFn: async (payload: { perMinute: number; perHour: number; perDay: number }) => {
      const response = await apiRequest<{ rateLimits: RateLimitStats }>("/api/admin/rate-limits", {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      return response.rateLimits;
    },
    onSuccess: async () => {
      setLimitFormError(null);
      setIsEditingLimits(false);
      await queryClient.invalidateQueries({ queryKey: ["admin", "summary"] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Unable to update rate limits.";
      setLimitFormError(message);
    },
  });

  const savingLimits = rateLimitMutation.isPending;

  useEffect(() => {
    fetchProfile().catch(() => {});
  }, [fetchProfile]);

  useEffect(() => {
    if (!isEditingLimits) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !savingLimits) {
        event.preventDefault();
        setIsEditingLimits(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isEditingLimits, savingLimits]);

  const numberFormatter = useMemo(() => new Intl.NumberFormat("en-US"), []);

  const formatNumber = (value: number | undefined) => {
    if (typeof value !== "number" || Number.isNaN(value)) return "—";
    return numberFormatter.format(value);
  };

  const lastActivity = data?.lastActivity
    ? formatDistanceToNow(new Date(data.lastActivity), { addSuffix: true })
    : "No recent activity";

  const handleOpenRateLimitEditor = () => {
    setLimitInputs({
      perMinute: String(data?.rateLimits.perMinute ?? 15),
      perHour: String(data?.rateLimits.perHour ?? 120),
      perDay: String(data?.rateLimits.perDay ?? 600),
    });
    setLimitFormError(null);
    setIsEditingLimits(true);
  };

  const handleLimitInputChange = (field: "perMinute" | "perHour" | "perDay") => (event: ChangeEvent<HTMLInputElement>) => {
    setLimitInputs((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleRateLimitSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const perMinute = Number.parseInt(limitInputs.perMinute, 10);
    const perHour = Number.parseInt(limitInputs.perHour, 10);
    const perDay = Number.parseInt(limitInputs.perDay, 10);

    if (Number.isNaN(perMinute) || perMinute <= 0) {
      setLimitFormError("Per minute limit must be a positive number.");
      return;
    }
    if (Number.isNaN(perHour) || perHour <= 0) {
      setLimitFormError("Per hour limit must be a positive number.");
      return;
    }
    if (Number.isNaN(perDay) || perDay <= 0) {
      setLimitFormError("Per day limit must be a positive number.");
      return;
    }
    if (perHour < perMinute) {
      setLimitFormError("Per hour limit must be >= per minute limit.");
      return;
    }
    if (perDay < perHour) {
      setLimitFormError("Per day limit must be >= per hour limit.");
      return;
    }
    rateLimitMutation.mutate({ perMinute, perHour, perDay });
  };

  const rateLimitMeta = useMemo(() => {
    const info = data?.rateLimits;
    if (!info) return null;
    if (info.updatedAt) {
      const updated = new Date(info.updatedAt);
      if (!Number.isNaN(updated.getTime())) {
        const relative = formatDistanceToNow(updated, { addSuffix: true });
        return info.updatedBy ? `Updated ${relative} by ${info.updatedBy}` : `Updated ${relative}`;
      }
    }
    return info.source === "default" ? "Using defaults" : null;
  }, [data?.rateLimits]);

  const statCards = [
    {
      label: "Documents",
      icon: "db",
      tone: "g",
      value: formatNumber(data?.documents),
      sub: "Knowledge base items",
      spark: { stroke: "var(--g)", path: "M0 27 L11 25 L22 26 L33 20 L44 22 L55 15 L66 17 L77 10 L88 6" },
    },
    {
      label: "Tokens Used",
      icon: "zap",
      tone: "b",
      value: formatNumber(data?.tokensUsed),
      sub: "Total processed",
      spark: { stroke: "var(--info)", path: "M0 24 L11 26 L22 19 L33 22 L44 14 L55 18 L66 11 L77 13 L88 7" },
    },
    {
      label: "Sessions",
      icon: "msg",
      tone: "p",
      value: formatNumber(data?.sessions),
      sub: "Conversations",
      spark: { stroke: "var(--purple)", path: "M0 12 L11 9 L22 14 L33 11 L44 17 L55 15 L66 21 L77 19 L88 24" },
    },
  ];

  const statusRows = [
    { label: "API", icon: "act", status: "Operational" },
    { label: "Embeddings", icon: "layers", status: "Healthy" },
    { label: "Vector DB", icon: "db", status: "Connected" },
  ];

  return (
    <div className="page">
      <PageHeader
        label="Dashboard"
        title={`Welcome, ${profile?.displayName ?? "Admin"}`}
        description="Monitor your AI assistant's usage and configure settings."
      />

      {/* STAT CARDS */}
      <div className="stats">
        {statCards.map((stat) => (
          <div key={stat.label} className="stat">
            <div className="stat-h">
              <span className={`stat-i ${stat.tone}`}>
                <Icon name={stat.icon} />
              </span>
              <span className="lb">{stat.label}</span>
            </div>
            <div className="stat-v">{stat.value}</div>
            <div className="stat-b">
              <span className="sl">{stat.sub}</span>
            </div>
            <svg className="spark" viewBox="0 0 88 34" preserveAspectRatio="none" fill="none">
              <path d={stat.spark.path} stroke={stat.spark.stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d={`${stat.spark.path} L88 34 L0 34 Z`} fill={stat.spark.stroke} opacity=".08" />
            </svg>
          </div>
        ))}

        <div className="stat">
          <div className="stat-h">
            <span className="stat-i a">
              <Icon name="clock" />
            </span>
            <span className="lb">Last Activity</span>
          </div>
          <div className="stat-v" style={{ fontSize: 22 }}>{lastActivity}</div>
          <div className="stat-b">
            <span className="sl">Most recent message</span>
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1.6fr 1fr" }}>
        {/* RATE LIMITS */}
        <div className="card">
          <div className="card-h">
            <div>
              <h2>Rate Limits</h2>
              <p>Guest request throttling per time window</p>
            </div>
            <div className="r">
              {rateLimitMeta && <span className="meta">{rateLimitMeta}</span>}
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenRateLimitEditor}
                disabled={savingLimits || isLoading}
              >
                <Icon name="edit" />
                Edit
              </Button>
            </div>
          </div>
          <div className="card-b">
            <div className="rt">
              {[
                { label: "Per Minute", value: data?.rateLimits.perMinute },
                { label: "Per Hour", value: data?.rateLimits.perHour },
                { label: "Per Day", value: data?.rateLimits.perDay },
              ].map((item) => (
                <div key={item.label} className="rtile">
                  <div className="lb">{item.label}</div>
                  <div className="v">
                    {item.value ?? "—"} <small>req</small>
                  </div>
                </div>
              ))}
            </div>
            <div className="al n" style={{ marginTop: 14 }}>
              <Icon name="info" />
              <div className="cnt-x">Signed-in members bypass guest throttling.</div>
            </div>
          </div>
        </div>

        {/* SYSTEM STATUS */}
        <div className="card">
          <div className="card-h">
            <div>
              <h2>System Status</h2>
              <p>Service health indicators</p>
            </div>
          </div>
          <div className="card-b" style={{ paddingTop: 4, paddingBottom: 4 }}>
            {statusRows.map((item) => (
              <div key={item.label} className="hr">
                <span className="hi">
                  <Icon name={item.icon} />
                </span>
                <span className="tx">
                  <b>{item.label}</b>
                </span>
                <span className="b suc">
                  <span className="dot"></span>
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rate Limit Modal */}
      {isEditingLimits && (
        <div
          className="ov open"
          onClick={(event) => {
            if (event.target === event.currentTarget && !savingLimits) setIsEditingLimits(false);
          }}
        >
          <div className="mod">
            <div className="mod-h">
              <div className="tx">
                <h3>Edit Rate Limits</h3>
                <p>Guest request throttling per time window. Signed-in members are not affected.</p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setIsEditingLimits(false)}
                disabled={savingLimits}
              >
                <Icon name="x" />
              </button>
            </div>
            <form onSubmit={handleRateLimitSubmit}>
              <div className="mod-b">
                <div className="row c3">
                  {[
                    { id: "perMinute", label: "Per Minute" },
                    { id: "perHour", label: "Per Hour" },
                    { id: "perDay", label: "Per Day" },
                  ].map((field) => (
                    <div key={field.id} className="fg">
                      <label className="fl" htmlFor={field.id}>{field.label}</label>
                      <input
                        id={field.id}
                        className="inp num"
                        type="number"
                        min={1}
                        value={limitInputs[field.id as keyof typeof limitInputs]}
                        onChange={handleLimitInputChange(field.id as "perMinute" | "perHour" | "perDay")}
                        disabled={savingLimits}
                        required
                      />
                    </div>
                  ))}
                </div>

                {limitFormError ? (
                  <div className="al e" style={{ marginTop: 4 }}>
                    <Icon name="alert" />
                    <div className="cnt-x">{limitFormError}</div>
                  </div>
                ) : (
                  <div className="al w" style={{ marginTop: 4 }}>
                    <Icon name="alert" />
                    <div className="cnt-x">Lowering a ceiling takes effect immediately. Guests over the new limit will be throttled on their next request.</div>
                  </div>
                )}
              </div>
              <div className="mod-f">
                <button
                  type="button"
                  className="btn gho"
                  onClick={() => setIsEditingLimits(false)}
                  disabled={savingLimits}
                >
                  Cancel
                </button>
                <Button type="submit" loading={savingLimits}>
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
